// 배차 최소경비 산출 유틸 (로컬 계산)
// 카카오 Geocoder로 출발지/도착지를 좌표로 변환한 뒤
// 직선거리, 운행거리(추정), 주유비, 톨비, 인건비, 감가상각비를 산출한다.

let kakaoSdkPromise = null;
function loadKakaoSdk() {
  if (kakaoSdkPromise) return kakaoSdkPromise;
  kakaoSdkPromise = new Promise((resolve, reject) => {
    const tryLoad = () => {
      if (window.kakao && window.kakao.maps && typeof window.kakao.maps.load === 'function') {
        window.kakao.maps.load(() => {
          if (window.kakao.maps.services && window.kakao.maps.services.Geocoder) {
            resolve(window.kakao);
          } else {
            reject(new Error('Kakao services 라이브러리가 로드되지 않았습니다. libraries=services 가 SDK URL 에 포함되어 있는지 확인하세요.'));
          }
        });
        return true;
      }
      return false;
    };
    if (tryLoad()) return;
    const start = Date.now();
    const check = setInterval(() => {
      if (tryLoad()) { clearInterval(check); return; }
      if (Date.now() - start > 10000) {
        clearInterval(check);
        reject(new Error('Kakao Maps SDK 로드 시간 초과'));
      }
    }, 200);
  });
  return kakaoSdkPromise;
}

function geocode(kakao, address) {
  return new Promise((resolve, reject) => {
    const geocoder = new kakao.maps.services.Geocoder();
    geocoder.addressSearch(address, (result, status) => {
      if (status === kakao.maps.services.Status.OK && result && result.length > 0) {
        resolve({ lat: parseFloat(result[0].y), lng: parseFloat(result[0].x) });
      } else {
        reject(new Error(`주소 좌표 변환 실패: ${address}`));
      }
    });
  });
}

// Haversine — 두 좌표 간 직선거리 (km)
function haversineKm(a, b) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(x));
}

// 차종별 파라미터
// efficiency: 연비 (km/L), tollClass: 한국 고속도로 차종 (1~5종)
const VEHICLE_PARAMS = {
  '1톤': { efficiency: 9, tollClass: 1, depreciationPerKm: 40 },
  '1.4톤': { efficiency: 8, tollClass: 1, depreciationPerKm: 50 },
  '2.5톤': { efficiency: 6, tollClass: 2, depreciationPerKm: 70 },
  '3.5톤': { efficiency: 5.5, tollClass: 2, depreciationPerKm: 80 },
  '5톤': { efficiency: 4, tollClass: 3, depreciationPerKm: 90 },
  '8톤': { efficiency: 3.5, tollClass: 3, depreciationPerKm: 100 },
  '11톤': { efficiency: 3, tollClass: 4, depreciationPerKm: 120 },
  '14톤': { efficiency: 2.8, tollClass: 4, depreciationPerKm: 130 },
  '18톤': { efficiency: 2.6, tollClass: 5, depreciationPerKm: 140 },
  '25톤': { efficiency: 2.5, tollClass: 5, depreciationPerKm: 150 },
};

const TOLL_PER_KM_BY_CLASS = { 1: 65, 2: 95, 3: 100, 4: 115, 5: 140 };

const DEFAULT_VEHICLE = { efficiency: 5, tollClass: 3, depreciationPerKm: 90 };

function normalizeVehicleType(raw) {
  if (!raw) return null;
  const s = String(raw).replace(/\s/g, '');
  const keys = Object.keys(VEHICLE_PARAMS);
  for (const k of keys) if (s === k) return k;
  const m = s.match(/([\d.]+)/);
  if (m) {
    const num = parseFloat(m[1]);
    let best = null;
    let bestDiff = Infinity;
    for (const k of keys) {
      const n = parseFloat(k);
      const diff = Math.abs(n - num);
      if (diff < bestDiff) { bestDiff = diff; best = k; }
    }
    if (best) return best;
  }
  return null;
}

export async function estimateMinimumCost({
  originAddress,
  destinationAddress,
  vehicleType,
  waypointAddresses = [], // ['주소1','주소2', ...] — 경유지 (옵션)
  options = {},
}) {
  if (!originAddress || !destinationAddress) {
    throw new Error('출발지 또는 도착지 주소가 비어 있습니다.');
  }
  const {
    dieselPricePerL = 1750,
    hourlyWage = 25000,
    avgSpeedKmh = 60,
    loadingHours = 1.0,
    drivingMultiplier = 1.3,
    marginRate = 0.15,
    includeReturnTrip = true,
  } = options;

  const kakao = await loadKakaoSdk();
  // 경유지 포함 모든 좌표
  const allAddresses = [originAddress, ...(waypointAddresses || []).filter(Boolean), destinationAddress];
  const coords = await Promise.all(allAddresses.map((a) => geocode(kakao, a)));
  const origin = coords[0];
  const destination = coords[coords.length - 1];

  // 직선 거리: 출발 → (경유 순차) → 도착 합
  let straightKm = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    straightKm += haversineKm(coords[i], coords[i + 1]);
  }
  const onewayKm = straightKm * drivingMultiplier;
  const totalKm = includeReturnTrip ? onewayKm * 2 : onewayKm;

  const vKey = normalizeVehicleType(vehicleType);
  const v = vKey ? VEHICLE_PARAMS[vKey] : DEFAULT_VEHICLE;
  const vLabel = vKey || '미지정(중형 기준)';

  const fuelLiters = totalKm / v.efficiency;
  const fuelCost = Math.round(fuelLiters * dieselPricePerL);

  const tollPerKm = TOLL_PER_KM_BY_CLASS[v.tollClass] || 100;
  let highwayRatio = 0.3;
  if (onewayKm >= 100) highwayRatio = 0.65;
  else if (onewayKm >= 50) highwayRatio = 0.5;
  const tollKm = onewayKm < 30 ? 0 : totalKm * highwayRatio;
  const tollCost = Math.round(tollKm * tollPerKm);

  const drivingHours = totalKm / avgSpeedKmh;
  const totalHours = drivingHours + loadingHours;
  const laborCost = Math.round(totalHours * hourlyWage);

  const depreciationCost = Math.round(totalKm * v.depreciationPerKm);

  const subtotal = fuelCost + tollCost + laborCost + depreciationCost;
  const margin = Math.round(subtotal * marginRate);
  const recommendedPrice = roundUpTo(subtotal + margin, 1000);

  return {
    origin,
    destination,
    straightKm: round1(straightKm),
    onewayKm: round1(onewayKm),
    totalKm: round1(totalKm),
    vehicleLabel: vLabel,
    fuelLiters: round1(fuelLiters),
    fuelCost,
    tollKm: round1(tollKm),
    tollCost,
    drivingHours: round1(drivingHours),
    totalHours: round1(totalHours),
    laborCost,
    depreciationCost,
    subtotal,
    margin,
    marginRate,
    recommendedPrice,
    assumptions: {
      dieselPricePerL,
      hourlyWage,
      avgSpeedKmh,
      loadingHours,
      drivingMultiplier,
      includeReturnTrip,
      highwayRatio,
      tollPerKm,
      vehicleEfficiency: v.efficiency,
      depreciationPerKm: v.depreciationPerKm,
    },
  };
}

function round1(n) { return Math.round(n * 10) / 10; }
function roundUpTo(n, unit) { return Math.ceil(n / unit) * unit; }
